import { computed, reactive } from 'vue'
import { useAuth } from '@/Hooks/Company/useAuth'
import { usePermissions } from '@/Hooks/Company/usePermissions'
import { useFeatureFlags } from '@/Hooks/Company/useFeatureFlags'

const { isAryeoLiteUser } = useAuth()

export default function useZillowIntegrationsNavigation() {
  const { hasPermission } = usePermissions()
  const { hasFeatureFlag } = useFeatureFlags()

  const canManageZillowIntegrations = hasPermission('zillow_media_exclusives_manage')

  const photographerBrandingCard = computed(() => ({
    show: true,
    comingSoon: false,
    name: 'Zillow Media Advantage',
    description: 'Grow your media business and deliver more value to your agents on Zillow.',
    routeIsInertia: true,
    targetRouteName: canManageZillowIntegrations
      ? 'web.company.zillow-integrations.zillow-media-advantage.show'
      : null,
    image:
      'https://aryeo.sfo2.cdn.digitaloceanspaces.com/aryeo_media/app_store_tiles/zillow-media-advantage.png',
    imageObjectFit: 'contain',
    routeNamesToCheckIfCurrent: ['web.company.zillow-integrations.zillow-media-advantage.*'],
    integrationProvider: 'ZILLOW_MEDIA_ADVANTAGE',
    shouldShowBadge: true,
    requiresProAccount: false,
  }))

  const showcaseSelectCard = computed(() => ({
    show: true,
    comingSoon: false,
    name: 'Showcase Select',
    description:
      'Get more business by making your company discoverable and eligible for direct Zillow Showcase media booking.',
    routeIsInertia: true,
    targetRouteName: canManageZillowIntegrations
      ? 'web.company.zillow-integrations.showcase-select.show'
      : null,
    image:
      'https://aryeo.sfo2.cdn.digitaloceanspaces.com/aryeo_media/files/zillow-media-exclusives/showcase-select.png',
    routeNamesToCheckIfCurrent: ['web.company.zillow-integrations.showcase-select.*'],
    integrationProvider: 'SHOWCASE',
    shouldShowBadge: true,
    requiresProAccount: true,
  }))

  const zillowRentalsCard = computed(() => ({
    show: !isAryeoLiteUser.value && hasFeatureFlag('zillow_rentals'),
    comingSoon: false,
    name: 'Zillow Rentals',
    description:
      'Get recommended to Zillow Rental Partners by joining a curated list of media ' +
      'providers who can capture Zillow 3D Home tours and interactive floor plans.',
    routeIsInertia: true,
    targetRouteName: canManageZillowIntegrations
      ? 'web.company.zillow-integrations.zillow-rentals.show'
      : null,
    image:
      'https://aryeo.sfo2.cdn.digitaloceanspaces.com/aryeo_media/files/zillow-media-exclusives/zillow-rentals.png',
    imageObjectFit: 'contain',
    routeNamesToCheckIfCurrent: ['web.company.zillow-integrations.zillow-rentals.*'],
    integrationProvider: 'ZILLOW_RENTALS',
    shouldShowBadge: true,
    requiresProAccount: false,
  }))

  const streetEasyCard = computed(() => ({
    show: !isAryeoLiteUser.value && hasFeatureFlag('zillow_streeteasy'),
    comingSoon: false,
    name: 'StreetEasy',
    description:
      'Get recommended to StreetEasy partners by joining a curated list of media ' +
      'providers who can capture Zillow 3D Home tours and interactive floor plans.',
    routeIsInertia: true,
    targetRouteName: canManageZillowIntegrations
      ? 'web.company.zillow-integrations.streeteasy.show'
      : null,
    image:
      'https://aryeo.sfo2.cdn.digitaloceanspaces.com/aryeo_media/app_store_tiles/streeteasy-brand-tile.png',
    imageObjectFit: 'contain',
    routeNamesToCheckIfCurrent: ['web.company.zillow-integrations.streeteasy.*'],
    integrationProvider: 'ZILLOW_STREETEASY',
    shouldShowBadge: true,
    requiresProAccount: false,
  }))

  const zillow3dHomeCard = computed(() => ({
    show: true,
    comingSoon: !hasFeatureFlag('zillow_3d_home'),
    name: 'Zillow 3D Home',
    description:
      'Streamline your workflow and sync tours directly to your listings with the 3D Home Integration.',
    routeIsInertia: true,
    targetRouteName: canManageZillowIntegrations
      ? 'web.company.zillow-integrations.zillow-3d-home.show'
      : null,
    image:
      'https://aryeo.sfo2.cdn.digitaloceanspaces.com/aryeo_media/files/zillow-media-exclusives/zillow-3d-home.png',
    routeNamesToCheckIfCurrent: ['web.company.zillow-integrations.zillow-3d-home.*'],
    integrationProvider: 'ZILLOW_3D_HOME',
    shouldShowBadge: true,
    requiresProAccount: true,
  }))

  const zillowProfileCard = computed(() => ({
    show: true,
    name: 'Zillow Profile',
    description:
      'Create and manage your company profile that will be used across the Zillow integrations you have enabled for your company.',
    routeIsInertia: true,
    targetRouteName: canManageZillowIntegrations
      ? 'web.company.zillow-integrations.profile.edit'
      : null,
    routeNamesToCheckIfCurrent: ['web.company.zillow-integrations.profile.*'],
    shouldShowBadge: false,
  }))

  const settingsSection = computed(() => ({
    show: true,
    name: 'Settings',
    alwaysShowHeader: true,
    description: '',
    children: [zillowProfileCard.value],
  }))

  const sections = computed(() => [
    {
      show: true,
      name: 'Zillow Media Advantage',
      tag: 'Featured',
      description: '',
      children: [photographerBrandingCard.value],
    },
    {
      show: true,
      name: 'Additional Zillow features',
      description: '',
      children: [
        showcaseSelectCard.value,
        zillowRentalsCard.value,
        streetEasyCard.value,
        zillow3dHomeCard.value,
      ],
    },
    settingsSection.value,
  ])

  const sidebarSections = computed(() => {
    const grouped = sections.value.filter((s) => s.show && !s.alwaysShowHeader)
    const pinned = sections.value.filter((s) => s.show && s.alwaysShowHeader)
    const flatChildren = grouped.flatMap((s) => s.children)

    return [
      ...(flatChildren.length ? [{ show: true, name: undefined, children: flatChildren }] : []),
      ...pinned,
    ]
  })

  return reactive({
    overviewTitle: 'Zillow Integrations',
    overviewRouteName: 'web.company.zillow-integrations.index',
    sections,
    sidebarSections,
  })
}
